import type { ReactNode } from 'react';
import { EnvBadge } from './EnvironmentSwitcher.js';
import { Icon } from './ui.js';
import { ENV_META, envFromApp } from '../lib/environments.js';
import type { EnvName } from '../lib/environments.js';
import type { Me } from '../lib/session.js';
import '../screens-2c.css';

/** Prostředí, ve kterém právě běžíme (bez přepínání prostředí se odvodí z nasazení). */
export function activeEnv(me: Me): EnvName {
  return me.environment?.name ?? envFromApp(me.appEnv);
}

/**
 * Pruh „co se tady mění“: štítek aktivního prostředí + věta, že změna platí jen pro něj.
 * Každé prostředí má vlastní databázi i vlastní nastavení.
 */
export function EnvScope({
  me,
  children,
  compact,
}: {
  me: Me;
  children?: ReactNode;
  compact?: boolean;
}): JSX.Element {
  const env = activeEnv(me);
  const meta = ENV_META[env];
  return (
    <div className={`s2c-scope env-${env}${compact ? ' compact' : ''}`} data-testid="env-scope">
      <span className="s2c-scope-badge">
        <EnvBadge name={env} />
      </span>
      <p>
        {compact ? 'Platí jen pro toto prostředí. ' : `Měníte prostředí ${meta.label}. `}
        {me.environment ? (
          <>
            Má vlastní databázi (<code>{me.environment.database}</code>) a vlastní nastavení;
            ostatních prostředí se to netýká.
          </>
        ) : (
          'Nastavení se ukládá do databáze tohoto nasazení.'
        )}{' '}
        {children}
      </p>
    </div>
  );
}

export type StepState = 'done' | 'now' | 'todo';

/** Průvodce krok za krokem: číslo, název, stručný popis a stav (hotovo / teď / čeká). */
export function Steps({
  steps,
  label,
}: {
  steps: Array<{ title: string; text: string; state: StepState }>;
  label: string;
}): JSX.Element {
  const stateText: Record<StepState, string> = {
    done: 'hotovo',
    now: 'další krok',
    todo: 'zatím ne',
  };
  return (
    <ol className="s2c-steps" aria-label={label}>
      {steps.map((s, i) => (
        <li key={s.title} className={s.state} aria-current={s.state === 'now' ? 'step' : undefined}>
          <i aria-hidden="true">{s.state === 'done' ? <Icon name="check" size={14} /> : i + 1}</i>
          <span>
            <b>{s.title}</b>
            <small>{s.text}</small>
            <em>{stateText[s.state]}</em>
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Vysvětlení rozdílu mezi během nanečisto a ostrým během, přímo na stránce. */
export function RunModes({ dry, live }: { dry: ReactNode; live: ReactNode }): JSX.Element {
  return (
    <div className="s2c-modes">
      <div className="s2c-mode dry">
        <b>
          <Icon name="flask" size={16} /> Nanečisto (zkouška bez zápisu)
        </b>
        <p>{dry}</p>
      </div>
      <div className="s2c-mode live">
        <b>
          <Icon name="bolt" size={16} /> Ostrý běh (zapisuje)
        </b>
        <p>{live}</p>
      </div>
    </div>
  );
}

/** Zaškrtávátko s popiskem a vysvětlením následků. */
export function Check({
  checked,
  onChange,
  disabled,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  label: string;
  hint?: ReactNode;
}): JSX.Element {
  return (
    <label className="s2c-check">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <b>{label}</b>
        {hint ? <small>{hint}</small> : null}
      </span>
    </label>
  );
}

/** „1 h 5 min“, „3 min 20 s“, „45 s“. */
export function duration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  if (s >= 3600) return `${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min`;
  if (s >= 60) return `${Math.floor(s / 60)} min ${s % 60} s`;
  return `${s} s`;
}
